import type { ApiResponse } from './generic';

// NOTE: This enum is intentionally duplicated in backend/src/common/enums/user-role.enum.ts.
// The backend cannot import from this shared package at build time, so both definitions
// must be kept manually in sync. If a role is added or renamed here, the same change
// MUST be applied to the backend enum (and vice versa). The string values are what the
// API serializes into JWTs and login responses, so mismatches will silently break role-
// based authorization on the frontend without any compile-time error.
export enum UserRole {
  SUPER_ADMIN = 'super_admin',
  COMPANY_ADMIN = 'company_admin',
  EMPLOYEE = 'employee',
}

export interface LoginForm {
  email: string;
  password: string;
  remember?: boolean;
}

export type LoginResponse = ApiResponse<{
  accessToken: string;
  sessionId: string;
  role: string;
}>;

export type OtpChallengeResponse = ApiResponse<{
  requiresOtp: true;
  otpToken: string;
  maskedEmail: string;
}>;

export type LoginApiResponse = LoginResponse | OtpChallengeResponse;

export type ResendOtpResponse = ApiResponse<{ maskedEmail: string }>;

export enum AuthErrorCode {
  /** The OTP challenge (otpToken) can never succeed again — revoked (max
   * attempts), expired (30-min window), or never existed. Verify and resend
   * both fail permanently once this is returned; the caller should treat it
   * as terminal and return the user to the login form. */
  OtpChallengeTerminal = 'OTP_CHALLENGE_TERMINAL',
  /** Wrong OTP code entered; attempts remain. The challenge is still active —
   * the user may retry. Accompanied by `remainingAttempts` in the response. */
  InvalidOtp = 'INVALID_OTP',
  /** The OTP code itself has expired (short-lived code TTL). The challenge is
   * still active — trigger a resend to issue a fresh code. */
  OtpExpired = 'OTP_EXPIRED',
}

export interface SignupForm {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  confirmPassword: string;
}

export interface SignupResponse {
  message: string;
  data: {
    accessToken: string;
    sessionId: string;
  };
}

export interface User {
  id: string;
  firstName: string;
  lastName: string;
  profileImage: string | null;
  dateOfBirth: string | null;
  language: string;
  gender: string | null;
  phoneCountryCode: string | null;
  phoneNumber: string | null;
  phoneVerifiedAt: string | null;
  email: string;
  temporaryPasswordGeneratedAt: string | null;
  passwordChangedAt: string | null;
  createdAt: string;
  updatedAt: string;
}
export interface ResetPasswordForm {
  currentPassword: string;
  newPassword: string;
}

export interface ResetForgotPasswordForm {
  newPassword: string;
  token?: string;
}

export interface ForgotPasswordForm {
  email: string;
}
export interface ForgotPasswordResponse {
  message: string;
  data: null;
}
